// contexts/AuthContext.jsx
import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import API_BASE from "../api/api";
import {AuthService} from "../api/auth.api";
import {CartSessionManager} from "../api/cart.api";

// Create the Auth Context
const AuthContext = createContext();

// Hook to use the Auth Context
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

// Auth Provider Component
export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [error, setError] = useState(null);
    const [isInitializing, setIsInitializing] = useState(true);

    // Initialize auth state with proper storage handling
    const initAuth = useCallback(async () => {
        try {
            setIsInitializing(true);
            // console.log('AuthProvider: Initializing authentication...');

            // Check for stored user data
            const storedUser = AuthService.getStoredUser();
            const storedToken = AuthService.getStoredToken();

            if (!storedUser || !storedToken) {
                // console.log('AuthProvider: No stored user or token found');
                setUser(null);
                setIsAuthenticated(false);
                setIsInitializing(false);
                setIsLoading(false);
                return;
            }

            // Set initial state from storage
            setUser(storedUser);
            setIsAuthenticated(true);

            // Check if token is expired
            if (AuthService.isTokenExpired()) {
                // console.log('AuthProvider: Token expired, attempting refresh...');
                try {
                    await AuthService.refreshToken();
                    // console.log('AuthProvider: Token refreshed successfully');

                    // Get updated user data
                    const updatedUser = AuthService.getStoredUser();
                    setUser(updatedUser);
                } catch (refreshError) {
                    // console.log('AuthProvider: Token refresh failed, clearing auth data');
                    AuthService.clearUserData();
                    setUser(null);
                    setIsAuthenticated(false);
                    setIsInitializing(false);
                    setIsLoading(false);
                    return;
                }
            }

            // Verify authentication with server
            try {
                // console.log('AuthProvider: Verifying authentication with server...');
                const authResult = await AuthService.checkAuth();

                if (!authResult.authenticated) {
                    // console.log('AuthProvider: Server authentication failed');
                    AuthService.clearUserData();
                    setUser(null);
                    setIsAuthenticated(false);
                } else {
                    // Update user data if available from server
                    if (authResult.user) {
                        const mergedUser = { ...storedUser, ...authResult.user };
                        setUser(mergedUser);
                        AuthService.storeUserData(mergedUser);
                    }

                    // Update last active timestamp
                    AuthService.updateLastActive();
                    // console.log('AuthProvider: Authentication successful');
                }
            } catch (serverError) {
                console.warn('AuthProvider: Server auth check failed, using local data:', serverError.message);
                // Continue with local data if server is down
            }

        } catch (error) {
            console.error('AuthProvider: Initialization error:', error);
            AuthService.clearUserData();
            setUser(null);
            setIsAuthenticated(false);
        } finally {
            setIsInitializing(false);
            setIsLoading(false);
            console.log('AuthProvider: Initialization complete');
        }
    }, []);

    // Initialize auth on mount
    useEffect(() => {
        initAuth();

        // Setup auto-refresh and activity tracking
        setupAuthMonitoring();

        // Cleanup
        return () => {
            // Any cleanup if needed
        };
    }, [initAuth]);

    // Setup auth monitoring (auto-refresh, activity tracking)
    const setupAuthMonitoring = () => {
        // Setup auto token refresh
        const autoRefreshInterval = setInterval(async () => {
            if (isAuthenticated && AuthService.isTokenExpired()) {
                try {
                    await AuthService.refreshToken();
                    // console.log('AuthProvider: Auto-refresh successful');
                } catch (error) {
                    console.error('AuthProvider: Auto-refresh failed:', error);
                }
            }
        }, 14 * 60 * 1000); // 14 minutes

        // Update last active on user interaction
        const updateActivity = () => {
            if (isAuthenticated) {
                AuthService.updateLastActive();
            }
        };

        // Add event listeners for user activity
        const events = ['click', 'keypress', 'mousemove', 'scroll', 'touchstart'];
        events.forEach(event => {
            window.addEventListener(event, updateActivity);
        });

        // Cleanup function
        return () => {
            clearInterval(autoRefreshInterval);
            events.forEach(event => {
                window.removeEventListener(event, updateActivity);
            });
        };
    };

    // Login function
    const login = async (email, password, rememberMe = false) => {
        try {
            setIsLoading(true);
            setError(null);

            // console.log('AuthProvider: Logging in...');
            const data = await AuthService.login(email, password, rememberMe);

            // Extract user data
            const userData = data.user || data.data?.user;

            if (!userData) {
                throw new Error('No user data received');
            }

            // Update state
            setUser(userData);
            setIsAuthenticated(true);
            setError(null);

            // Merge guest cart if exists
            await mergeGuestCart();

            // Update last active
            AuthService.updateLastActive();

            // console.log('AuthProvider: Login successful');

            return {
                success: true,
                user: userData,
                message: data.message || 'Login successful'
            };
        } catch (error) {
            const errorMessage = error.message || 'Login failed. Please check your credentials.';
            setError(errorMessage);
            console.error('AuthProvider: Login error:', error);

            return {
                success: false,
                error: errorMessage
            };
        } finally {
            setIsLoading(false);
        }
    };

    // Register function
    const register = async (userData) => {
        try {
            setIsLoading(true);
            setError(null);

            // console.log('AuthProvider: Registering user...');
            const data = await AuthService.register(userData);

            if (!data.success && !data.user) {
                throw new Error(data.message || 'Registration failed');
            }

            // If registration returns user data directly, use it
            if (data.user) {
                const userDataFromResponse = data.user;

                // Store user data
                AuthService.storeUserData(
                    userDataFromResponse,
                    data.accessToken,
                    data.refreshToken,
                    false
                );

                // Update state
                setUser(userDataFromResponse);
                setIsAuthenticated(true);
                setError(null);

                // Merge guest cart if exists
                await mergeGuestCart();

                // Update last active
                AuthService.updateLastActive();

                // console.log('AuthProvider: Registration successful with auto-login');

                return {
                    success: true,
                    user: userDataFromResponse,
                    message: data.message || 'Registration successful'
                };
            } else {
                // If no auto-login, just return success
                return {
                    success: true,
                    message: data.message || 'Registration successful. Please login.'
                };
            }
        } catch (error) {
            const errorMessage = error.message || 'Registration failed. Please try again.';
            setError(errorMessage);
            console.error('AuthProvider: Registration error:', error);

            return {
                success: false,
                error: errorMessage
            };
        } finally {
            setIsLoading(false);
        }
    };

    // Logout function
    const logout = async () => {
        try {
            setIsLoading(true);
            // console.log('AuthProvider: Logging out...');

            // Call server logout
            await AuthService.logout();

            // Reset state
            setUser(null);
            setIsAuthenticated(false);
            setError(null);

            // Clear guest cart session
            localStorage.removeItem('cart_session_id');

            // console.log('AuthProvider: Logout successful');

            return { success: true };
        } catch (error) {
            console.error('AuthProvider: Logout error:', error);

            // Still reset local state even if server logout fails
            setUser(null);
            setIsAuthenticated(false);

            // Clear local data
            AuthService.clearUserData();

            return {
                success: true,
                message: 'Logged out locally'
            };
        } finally {
            setIsLoading(false);
        }
    };

    // Get current user from server
    const getCurrentUser = async () => {
        try {
            // console.log('AuthProvider: Fetching current user from server...');
            const userData = await AuthService.getCurrentUser();

            // Update state
            setUser(userData);
            setIsAuthenticated(true);

            // Store updated user data
            AuthService.storeUserData(userData);

            // console.log('AuthProvider: Current user fetched successfully');

            return userData;
        } catch (error) {
            console.error('AuthProvider: Error fetching current user:', error);

            // If server call fails but we have local data, keep it
            const storedUser = AuthService.getStoredUser();
            if (storedUser) {
                // console.log('AuthProvider: Using stored user data');
                return storedUser;
            }

            throw error;
        }
    };

    // Merge guest cart after login
    const mergeGuestCart = async () => {
        try {
            const guestSessionId = CartSessionManager.getSessionId();

            if (!guestSessionId) {
                // console.log('AuthProvider: No guest cart to merge');
                return { success: true };
            }

            // console.log('AuthProvider: Merging guest cart...');
            const response = await fetch(`${API_BASE}/cart/merge`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Session-ID': guestSessionId,
                },
                credentials: 'include',
            });

            if (response.ok) {
                const data = await response.json();
                // Clear guest session ID after successful merge
                CartSessionManager.clearSessionId();
                // console.log('AuthProvider: Guest cart merged successfully');
                return { success: true, cart: data.data };
            }

            throw new Error('Cart merge failed');
        } catch (error) {
            console.error('AuthProvider: Cart merge error:', error);
            // Don't throw error - cart merge shouldn't block login
            return { success: false, error: error.message };
        }
    };

    // Check if user has specific role
    const hasRole = (role) => {
        return user?.role === role;
    };

    // Check if user has permission
    const hasPermission = (permission) => {
        return user?.permissions?.includes(permission);
    };

    // Update user profile
    const updateProfile = async (updatedData) => {
        try {
            setIsLoading(true);
            setError(null);

            // console.log('AuthProvider: Updating profile...');
            const token = AuthService.getStoredToken();

            if (!token) {
                throw new Error('No authentication token found');
            }

            const response = await fetch(`${API_BASE}/auth/profile`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                credentials: 'include',
                body: JSON.stringify(updatedData),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Profile update failed');
            }

            // Update local user data
            const updatedUser = { ...user, ...data.data };
            setUser(updatedUser);

            // Update storage
            AuthService.storeUserData(updatedUser);

            // console.log('AuthProvider: Profile updated successfully');

            return {
                success: true,
                user: updatedUser,
                message: data.message || 'Profile updated successfully'
            };
        } catch (error) {
            const errorMessage = error.message || 'Profile update failed';
            setError(errorMessage);
            console.error('AuthProvider: Profile update error:', error);

            return {
                success: false,
                error: errorMessage
            };
        } finally {
            setIsLoading(false);
        }
    };

    // Change password
    const changePassword = async (currentPassword, newPassword) => {
        try {
            setIsLoading(true);
            setError(null);

            // console.log('AuthProvider: Changing password...');
            const token = AuthService.getStoredToken();

            if (!token) {
                throw new Error('No authentication token found');
            }

            const response = await fetch(`${API_BASE}/auth/change-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                credentials: 'include',
                body: JSON.stringify({ currentPassword, newPassword }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Password change failed');
            }

            console.log('AuthProvider: Password changed successfully');

            return {
                success: true,
                message: data.message || 'Password changed successfully'
            };
        } catch (error) {
            const errorMessage = error.message || 'Password change failed';
            setError(errorMessage);
            console.error('AuthProvider: Password change error:', error);

            return {
                success: false,
                error: errorMessage
            };
        } finally {
            setIsLoading(false);
        }
    };

    // Forgot password
    const forgotPassword = async (email) => {
        try {
            setIsLoading(true);
            setError(null);

            // console.log('AuthProvider: Processing forgot password...');
            const data = await AuthService.forgotPassword(email);

            // console.log('AuthProvider: Forgot password email sent');

            return {
                success: true,
                message: data.message || 'Reset email sent successfully'
            };
        } catch (error) {
            const errorMessage = error.message || 'Failed to send reset email';
            setError(errorMessage);
            console.error('AuthProvider: Forgot password error:', error);

            return {
                success: false,
                error: errorMessage
            };
        } finally {
            setIsLoading(false);
        }
    };

    // Reset password
    const resetPassword = async (token, newPassword) => {
        try {
            setIsLoading(true);
            setError(null);

            // console.log('AuthProvider: Resetting password...');
            const data = await AuthService.resetPassword(token, newPassword);

            // console.log('AuthProvider: Password reset successful');

            return {
                success: true,
                message: data.message || 'Password reset successfully'
            };
        } catch (error) {
            const errorMessage = error.message || 'Password reset failed';
            setError(errorMessage);
            console.error('AuthProvider: Reset password error:', error);

            return {
                success: false,
                error: errorMessage
            };
        } finally {
            setIsLoading(false);
        }
    };

    // Verify email
    const verifyEmail = async (token) => {
        try {
            setIsLoading(true);
            setError(null);

            // console.log('AuthProvider: Verifying email...');
            const response = await fetch(`${API_BASE}/auth/verify-email/${token}`, {
                method: 'GET',
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Email verification failed');
            }

            // Update user verification status
            if (user) {
                const updatedUser = { ...user, isEmailVerified: true };
                setUser(updatedUser);

                // Update storage
                AuthService.storeUserData(updatedUser);
            }

            // console.log('AuthProvider: Email verified successfully');

            return {
                success: true,
                message: data.message || 'Email verified successfully'
            };
        } catch (error) {
            const errorMessage = error.message || 'Email verification failed';
            setError(errorMessage);
            console.error('AuthProvider: Email verification error:', error);

            return {
                success: false,
                error: errorMessage
            };
        } finally {
            setIsLoading(false);
        }
    };

    // Check authentication status
    const checkAuthStatus = async () => {
        try {
            const authResult = await AuthService.checkAuth();

            if (!authResult.authenticated) {
                // Clear auth data if not authenticated
                AuthService.clearUserData();
                setUser(null);
                setIsAuthenticated(false);
                return false;
            }

            // Update user data if available
            if (authResult.user && user) {
                const updatedUser = { ...user, ...authResult.user };
                setUser(updatedUser);
                AuthService.storeUserData(updatedUser);
            }

            // Update last active
            AuthService.updateLastActive();

            return true;
        } catch (error) {
            console.error('AuthProvider: Check auth status error:', error);
            return false;
        }
    };

    // Refresh user data
    const refreshUser = async () => {
        try {
            await getCurrentUser();
            return { success: true };
        } catch (error) {
            console.error('AuthProvider: Refresh user error:', error);
            return { success: false, error: error.message };
        }
    };

    // Clear error
    const clearError = () => {
        setError(null);
    };

    // Context value
    const value = {
        user,
        isLoading,
        isAuthenticated,
        error,
        isInitializing,
        login,
        register,
        logout,
        getCurrentUser,
        hasRole,
        hasPermission,
        updateProfile,
        changePassword,
        forgotPassword,
        resetPassword,
        verifyEmail,
        checkAuthStatus,
        refreshUser,
        clearError,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

// Higher Order Component for protecting routes
export const withAuth = (Component) => {
    return function ProtectedComponent(props) {
        const { isAuthenticated, isLoading, isInitializing } = useAuth();

        if (isInitializing || isLoading) {
            return (
                <div className="min-h-screen flex items-center justify-center bg-pearl">
                    <div className="text-center">
                        <div className="w-16 h-16 border-4 border-champagne/30 border-t-champagne rounded-full animate-spin mx-auto"></div>
                        <p className="mt-4 text-charcoal/60">Loading...</p>
                    </div>
                </div>
            );
        }

        if (!isAuthenticated) {
            // Store current location for redirect after login
            const currentPath = window.location.pathname + window.location.search;
            sessionStorage.setItem('kromaic_redirect_after_login', currentPath);

            // Redirect to login
            window.location.href = '/login';
            return null;
        }

        return <Component {...props} />;
    };
};

// Higher Order Component for admin routes
export const withAdmin = (Component) => {
    return function AdminComponent(props) {
        const { isAuthenticated, isLoading, isInitializing, hasRole } = useAuth();

        if (isInitializing || isLoading) {
            return (
                <div className="min-h-screen flex items-center justify-center bg-pearl">
                    <div className="text-center">
                        <div className="w-16 h-16 border-4 border-champagne/30 border-t-champagne rounded-full animate-spin mx-auto"></div>
                        <p className="mt-4 text-charcoal/60">Loading...</p>
                    </div>
                </div>
            );
        }

        if (!isAuthenticated || !hasRole('admin')) {
            // Redirect to home
            window.location.href = '/';
            return null;
        }

        return <Component {...props} />;
    };
};

// Hook for protected routes
export const useRequireAuth = (redirectTo = '/login') => {
    const { isAuthenticated, isLoading, isInitializing } = useAuth();

    useEffect(() => {
        if (!isInitializing && !isLoading && !isAuthenticated) {
            // Store current location for redirect after login
            const currentPath = window.location.pathname + window.location.search;
            sessionStorage.setItem('kromaic_redirect_after_login', currentPath);

            window.location.href = redirectTo;
        }
    }, [isLoading, isInitializing, isAuthenticated, redirectTo]);

    return { isLoading: isLoading || isInitializing, isAuthenticated };
};

// Hook for admin routes
export const useRequireAdmin = (redirectTo = '/') => {
    const { isAuthenticated, isLoading, isInitializing, hasRole } = useAuth();

    useEffect(() => {
        if (!isInitializing && !isLoading && (!isAuthenticated || !hasRole('admin'))) {
            window.location.href = redirectTo;
        }
    }, [isLoading, isInitializing, isAuthenticated, hasRole, redirectTo]);

    return {
        isLoading: isLoading || isInitializing,
        isAuthenticated,
        isAdmin: hasRole('admin')
    };
};

export default AuthContext;